import React, { useEffect, useState } from 'react';
import styles from './RetentionCall.module.scss';
import { Button } from '@zurich-primitives/button';
import { ReactComponent as AttachIcon } from 'app/assets/Icons/AttachWhite.svg';
import {
  handleFileSelection,
  handleFileValidation,
  IBusinessArea
} from 'app/utils';
import config from 'config';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  getBusinessArea,
  getCurrentFile,
  getService,
  setBusinessArea,
  setCurrentFile,
  setCurrentSummary,
  setDisclaimerModal,
  setGeneratingExtraction,
  useAppSelector
} from 'app/redux';
import BAMapping from 'data/mock/businessAreaAndServices.json';
import { ServiceOptions } from '../ServiceOptions';
import { SERVICE_NAMES } from 'app/utils/consts';
import { getFileExtraction } from 'integration';
import { UploadedFileCard } from '../UploadedDocument/UploadedDocument';
import {
  IAnalysisResult,
  IClassificationName,
  ICriteria,
  IMappedRetentionCallResponse,
  IRetentionCallGetResponse,
  IRetentionCallPostResponse
} from './RetentionCall.interfaces';
import { getCallRetentionExtraction } from 'integration/extraction/api';
import { generateCSVCallAnalysis, transformText } from 'app/utils/functions';
import { ActionButtons } from 'app/components/ActionButtons/ActionButtons';
import { DownloadCSVButton, GeneratingExtraction } from 'app/components';

const delay = async (milliseconds: number): Promise<void> => {
  return await new Promise<void>((resolve) =>
    setTimeout(resolve, milliseconds)
  );
};

export const RetentionCall: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentService = useAppSelector(getService);
  const businessAreas: IBusinessArea = BAMapping.businessAreas;
  const businessArea = useAppSelector(getBusinessArea);
  const currentBusinessArea = useAppSelector(getBusinessArea);

  const [errorText, setErrorText] = useState<string | undefined>();
  const STATES = {
    UPLOAD: 'upload',
    PROCESSING: 'processing',
    COMPLETED: 'completed'
  };
  const [state, setState] = useState<string>(STATES.UPLOAD);
  const currentFile = useAppSelector(getCurrentFile);
  const MAX_GET_ATTEMPTS = 20;
  const POLLING_DELAY_IN_MS = 30000;
  const POLLING_DURATION_IN_MS = 3 * 60 * 1000;

  let attemptCount = 0;
  const [evaluations, setEvaluations] = useState<
    Array<{ title: string; data: IMappedRetentionCallResponse[] }>
  >([]);

  const getUndefinedBA = (): void => {
    if (currentService !== undefined && businessArea === undefined) {
      for (const property in businessAreas) {
        if (businessAreas[property].services.includes(String(currentService)))
          dispatch(setBusinessArea(property));
      }
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { files } = e.target;
    const selectedFile: File | undefined =
      files && files.length > 0 ? files[0] : undefined;

    if (selectedFile !== undefined) {
      const validation = handleFileValidation(selectedFile, currentService);
      if (validation.valid) {
        if (businessArea == null) getUndefinedBA();
        dispatch(setCurrentFile(files?.[0]));
        setState(STATES.PROCESSING);
      } else {
        const errorMessage = validation.error;
        console.error(errorMessage);
        setErrorText(errorMessage);
      }
    }
  };

  const handleSelectionClick = (e: { stopPropagation: () => void }): void => {
    if (businessArea == null) getUndefinedBA();
    if (currentService === undefined) {
      dispatch(setDisclaimerModal(true));
    } else {
      e.stopPropagation();
      setErrorText(undefined);
      handleFileSelection(handleFileChange, currentService);
    }
  };

  const onFinally = (): void => {
    setState(STATES.COMPLETED);
    dispatch(setGeneratingExtraction(false));
  };

  const uploadRetentionCall = async (
    body: any
  ): Promise<IRetentionCallPostResponse> => {
    return await getFileExtraction(
      String(currentBusinessArea),
      String(currentService),
      body
    );
  };

  const getAnalysisResult = async (executionId: string): Promise<any> => {
    let response = { status: 'pending' };

    while (attemptCount < MAX_GET_ATTEMPTS && response.status !== 'completed') {
      attemptCount++;
      await delay(POLLING_DELAY_IN_MS);
      response = await getCallRetentionExtraction(
        String(currentBusinessArea),
        String(currentService),
        executionId
      );
    }

    return response;
  };

  const callGetFileExtraction = (executionId: string): void => {
    getAnalysisResult(executionId)
      .then((response: IRetentionCallGetResponse) => {
        let mappedEvaluations: Array<{
          title: string;
          data: IMappedRetentionCallResponse[];
        }> = [];

        const analysisResult = response.analysisResult;
        if (analysisResult) {
          mappedEvaluations = processAnalysisResult(analysisResult);
        }
        setEvaluations(mappedEvaluations);
      })
      .catch((error) => {
        console.log(error);
        dispatch(setCurrentFile(undefined));
        navigate(config.APP.PATH.LANDING);
      })
      .finally(() => {
        onFinally();
      });
  };

  const processAnalysisResult = (
    analysisResult: IAnalysisResult
  ): Array<{ title: string; data: IMappedRetentionCallResponse[] }> => {
    const evaluations: Array<{
      title: string;
      data: IMappedRetentionCallResponse[];
    }> = [];
    for (const evaluationKey in analysisResult) {
      const title = transformText(evaluationKey);
      const evaluation = analysisResult[evaluationKey].evaluation;
      const groupedData = processEvaluationSections(evaluation);
      evaluations.push({ title, data: groupedData });
    }
    return evaluations;
  };

  const processEvaluationSections = (evaluation: {
    [section: string]: IClassificationName;
  }): IMappedRetentionCallResponse[] => {
    const sections: IMappedRetentionCallResponse[] = [];
    for (const section in evaluation) {
      const criterias = evaluation[section].criterias;
      const transformedSection = transformText(section);
      const sectionData: IMappedRetentionCallResponse = {
        section: transformedSection,
        criterias: processCriterias(criterias)
      };
      sections.push(sectionData);
    }
    return sections;
  };

  const processCriterias = (criterias: {
    [key: string]: ICriteria;
  }): Array<{
    criteria: string;
    thinking: string;
    value: string;
  }> => {
    const criteriaList: Array<{
      criteria: string;
      thinking: string;
      value: string;
    }> = [];
    for (const criteria in criterias) {
      const excludedCriterias = ['name_asked', 'date_of_birth_asked'];

      if (!excludedCriterias.includes(criteria)) {
        const output = criterias[criteria].output;
        const transformedCriteria = transformText(criteria);

        criteriaList.push({
          criteria: transformedCriteria,
          thinking: output.thinking,
          value: transformText(output.value)
        });
      }
    }
    return criteriaList;
  };

  const fetchRetentionCall = async ({
    file
  }: {
    file?: File;
  }): Promise<void> => {
    dispatch(setGeneratingExtraction(true));
    const body = {
      file
    };
    setCurrentSummary(undefined);
    const { executionId } = await uploadRetentionCall(body);
    callGetFileExtraction(executionId);
  };

  useEffect(() => setErrorText(undefined), [currentService]);

  useEffect(() => {
    if (currentFile && currentService === SERVICE_NAMES.RETENTION_CALL) {
      fetchRetentionCall({
        file: currentFile
      });
    }
  }, [currentFile]);

  const renderUploadFileButton = (): React.JSX.Element => (
    <>
      <Button onClick={handleSelectionClick}>
        <Button.Icon>
          <AttachIcon />
        </Button.Icon>
        <Button.Label>Upload File</Button.Label>
      </Button>
      <span className={styles.error}>{errorText}</span>
    </>
  );

  const handleEraseDocument = (): void => {
    dispatch(setCurrentFile(undefined));
  };

  return (
    <div className={styles['service-container']}>
      <div className={styles['service-options']}>
        <ServiceOptions />
      </div>

      <ActionButtons
        handleDelete={handleEraseDocument}
        disableUploadNewFile={true}
        disableDeleteFile={true}
      />

      {(state === STATES.UPLOAD || !currentFile) && (
        <div className={styles['upload-state']}>
          <h3>Upload your MP3 file</h3>
          {renderUploadFileButton()}
        </div>
      )}

      {state === STATES.PROCESSING && currentFile && (
        <div>
          <h3>Document uploaded</h3>
          <UploadedFileCard file={currentFile} />
        </div>
      )}

      {state === STATES.COMPLETED && currentFile && (
        <div className={styles.wrapper}>
          <div>
            <h3>Document uploaded</h3>
            <UploadedFileCard file={currentFile} />
          </div>
          <div>
            {evaluations.every(
              (evaluation) => evaluation.data?.length === 0
            ) ? (
              <></>
            ) : (
              <>
                <div className={styles['retention-call-table']}>
                  {evaluations.map(
                    (evaluation, index) =>
                      evaluation.data?.length > 0 && (
                        <div key={evaluation.title + String(index)}>
                          <h2>{evaluation.title}</h2>
                          {evaluation.data.map((sectionData, index) => (
                            <div key={sectionData.section + String(index)}>
                              <h3>{sectionData.section}</h3>
                              <table>
                                <tbody>
                                  {sectionData.criterias.map(
                                    (criteria, index) => (
                                      <React.Fragment
                                        key={criteria.criteria + String(index)}
                                      >
                                        <tr>
                                          <th colSpan={2}>
                                            {criteria.criteria}
                                          </th>
                                        </tr>
                                        <tr>
                                          <td className={styles.thinking}>
                                            {criteria.thinking}
                                          </td>
                                          <td>{criteria.value}</td>
                                        </tr>
                                      </React.Fragment>
                                    )
                                  )}
                                </tbody>
                              </table>
                            </div>
                          ))}
                        </div>
                      )
                  )}
                </div>
                <DownloadCSVButton
                  handleOnClick={() =>
                    generateCSVCallAnalysis(evaluations, currentFile?.name)
                  }
                  isDisabled={evaluations === undefined}
                />
              </>
            )}
          </div>
        </div>
      )}
      <GeneratingExtraction processTime={POLLING_DURATION_IN_MS} />
    </div>
  );
};
